import React, { useState } from 'react';
import { api } from '../../utils/api';
import { PricingCalculatorInputs, PricingCalculatorResult } from '../../types';

export const PricingCalculator: React.FC = () => {
  const [inputs, setInputs] = useState<PricingCalculatorInputs>({
    planId: '',
    projects: 1,
    users: 1,
    storageGB: 1,
    bandwidthGB: 1,
    retentionDays: 30,
  });

  const [result, setResult] = useState<PricingCalculatorResult | null>(null);
  const [loading, setLoading] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInputs((prev) => ({ ...prev, [name]: name === 'planId' ? value : Number(value) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const data = await api.calculatePricing(inputs);
      setResult(data);
    } catch (error) {
      console.error('Error calculating pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-base-100 shadow rounded-lg p-6">
        <h1 className="text-2xl font-bold text-base-content mb-2">Pricing Calculator</h1>
        <p className="text-base-content/70">Calculate your potential costs based on your usage</p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-6 gap-4">
          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="planId" className="block text-sm font-medium text-base-content">
              Plan ID
            </label>
            <input
              type="text"
              name="planId"
              id="planId"
              value={inputs.planId}
              onChange={handleChange}
              className="mt-1 block w-full shadow-sm sm:text-sm border-base-300 rounded-md"
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="projects" className="block text-sm font-medium text-base-content">
              Projects
            </label>
            <input
              type="number"
              name="projects"
              id="projects"
              value={inputs.projects}
              onChange={handleChange}
              className="mt-1 block w-full shadow-sm sm:text-sm border-base-300 rounded-md"
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="users" className="block text-sm font-medium text-base-content">
              Users
            </label>
            <input
              type="number"
              name="users"
              id="users"
              value={inputs.users}
              onChange={handleChange}
              className="mt-1 block w-full shadow-sm sm:text-sm border-base-300 rounded-md"
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="storageGB" className="block text-sm font-medium text-base-content">
              Storage (GB)
            </label>
            <input
              type="number"
              name="storageGB"
              id="storageGB"
              value={inputs.storageGB}
              onChange={handleChange}
              className="mt-1 block w-full shadow-sm sm:text-sm border-base-300 rounded-md"
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="bandwidthGB" className="block text-sm font-medium text-base-content">
              Bandwidth (GB)
            </label>
            <input
              type="number"
              name="bandwidthGB"
              id="bandwidthGB"
              value={inputs.bandwidthGB}
              onChange={handleChange}
              className="mt-1 block w-full shadow-sm sm:text-sm border-base-300 rounded-md"
            />
          </div>

          <div className="col-span-6 sm:col-span-3">
            <label htmlFor="retentionDays" className="block text-sm font-medium text-base-content">
              Retention Days
            </label>
            <input
              type="number"
              name="retentionDays"
              id="retentionDays"
              value={inputs.retentionDays}
              onChange={handleChange}
              className="mt-1 block w-full shadow-sm sm:text-sm border-base-300 rounded-md"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          {loading ? 'Calculating...' : 'Calculate Pricing'}
        </button>
      </form>

      {result && (
        <div className="bg-base-100 shadow rounded-lg p-6 mt-6">
          <h2 className="text-lg font-semibold text-base-content mb-4">Pricing Summary</h2>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-base-content">Base Plan</p>
              <p className="text-lg font-semibold text-base-content">
                {result.basePlan.displayName}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-base-content">Monthly Total</p>
              <p className="text-lg font-semibold text-base-content">
                £{result.monthlyTotal.toFixed(2)}
              </p>
            </div>
          </div>
          <div className="mt-6">
            <dl className="divide-y divide-base-300">
              <div className="py-4 flex justify-between">
                <dt className="text-sm font-medium text-base-content">Plan Cost</dt>
                <dd className="text-sm text-base-content">
                  £{result.breakdown.planCost.toFixed(2)}
                </dd>
              </div>
              <div className="py-4 flex justify-between">
                <dt className="text-sm font-medium text-base-content">Additional Costs</dt>
                <dd className="text-sm text-base-content">
                  Projects: £{result.breakdown.additionalCosts.projects.toFixed(2)}, Users: £
                  {result.breakdown.additionalCosts.users.toFixed(2)}, Storage: £
                  {result.breakdown.additionalCosts.storage.toFixed(2)}, Bandwidth: £
                  {result.breakdown.additionalCosts.bandwidth.toFixed(2)}
                </dd>
              </div>
            </dl>
          </div>
          {result.warnings.length > 0 && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-red-700">Warnings</h3>
              <ul className="list-disc list-inside text-sm text-red-700">
                {result.warnings.map((warning, index) => (
                  <li key={index}>{warning}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
